import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppBubble from "@/components/site/WhatsAppBubble";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbSchema } from "@/components/site/Breadcrumbs";
import { CONTACT, HERO_BACKDROP } from "@/lib/products";
import { trackWhatsAppClick } from "@/lib/analytics";

const breadcrumbItems = [
  { label: "Home", href: "/" },
  { label: "Our Story", href: "/about" },
];

export default function AboutPage() {
  return (
    <div className="relative min-h-screen">
      <Seo
        title="Our Story — Family-Run Hand Engraving Studio in Hyderabad"
        description="Shine on Coin is a family-run studio in Hyderabad hand-engraving silver coins, gold lockets & bracelets with your photo. Discover our story on WhatsApp."
        path="/about"
        jsonLd={[breadcrumbSchema(breadcrumbItems)]}
      />
      <Navbar />
      <main>
        <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-30"
            style={{ backgroundImage: `url(${HERO_BACKDROP})`, backgroundSize: "cover", filter: "brightness(45%) saturate(70%)" }}
          />
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <Breadcrumbs items={breadcrumbItems} testId="about-breadcrumbs" />
            <div className="mt-6 max-w-3xl">
              <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Our Story</div>
              <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white" data-testid="about-heading">
                Every coin has a name behind it. <span className="italic gold-gradient-text">Ours has three.</span>
              </h1>
            </div>
          </div>
        </section>

        <section className="py-4 md:py-8" data-testid="about-content">
          <div className="max-w-3xl mx-auto px-5 md:px-10 space-y-14">
            <div>
              <h2 className="font-display text-2xl md:text-4xl text-white">Where it started</h2>
              <div className="mt-5 space-y-4 text-white/70 text-base md:text-lg leading-relaxed">
                <p>Shine on Coin began the way most meaningful things do — small, personal, and a little bit stubborn.</p>
                <p>
                  It started with a single question: what if the photograph you can't stop looking at could last as
                  long as the love in it? Not just printed. Not just framed. Engraved — into silver that doesn't
                  fade, gold that doesn't tarnish, held by crystal that catches light for years, not seasons.
                </p>
                <p>
                  We're a family-run business based in Hyderabad, hand-engraving 99.9 fine silver and 18–22ct gold
                  into coins, lockets, plates, and bracelets — each one carrying a face, a name, a date, or a few
                  words that matter more than most.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl md:text-4xl text-white">Moments we've engraved</h2>
              <div className="mt-5 space-y-4 text-white/70 text-base md:text-lg leading-relaxed">
                <p>
                  We've made pieces to celebrate 25 years of marriage. To thank a team's brand ambassador. To honour
                  a father on his 88th birthday, surrounded by the faces of every person who loves him. To turn a
                  single first birthday into a coin that will outlast the birthday itself.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl md:text-4xl text-white">How we work</h2>
              <div className="mt-5 space-y-4 text-white/70 text-base md:text-lg leading-relaxed">
                <p>
                  Every piece we make starts the same way: you send us a photograph, and a few words. We do the
                  rest — hand-engraving, setting the stones, finishing the frame — so that what returns to you
                  isn't just a gift, but something built to be kept.
                </p>
              </div>
            </div>

            <div className="rounded-3xl glass border border-white/10 p-6 md:p-10">
              <h2 className="font-display text-2xl md:text-4xl text-white">Delivery &amp; ordering</h2>
              <ul className="mt-5 space-y-2 text-white/70 text-base md:text-lg">
                <li>• 2–3 business days within Hyderabad.</li>
                <li>• 4–6 business days across India.</li>
                <li>
                  • Order directly on WhatsApp — no checkout, no back-and-forth forms. Just tell us your story, and
                  we'll help you shape it into something that shines.
                </li>
              </ul>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick({ placement: "about_page" })}
                data-testid="about-whatsapp-cta"
                className="mt-8 inline-flex items-center rounded-full px-6 py-3 bg-[#25D366] text-black font-medium text-sm hover:brightness-110 transition"
              >
                Message Us on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppBubble />
    </div>
  );
}
